import { useState, useEffect } from 'react'
import { useParams as useParamsRaw, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2, ArrowLeft } from 'lucide-react'
import { publicOptionApi } from '@/lib/api'
import { Search, GitBranch, TrendingUp, Users, BarChart3, Share2, FolderTree, BookOpen, Scale } from 'lucide-react'

export function PublicOptionDetailPage() {
  const params = useParamsRaw({ strict: false }) as { id?: string }
  const id = params.id || ''
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [eventData, setEventData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    publicOptionApi.getById(Number(id)).then((res: any) => {
      setEventData(res?.data || res)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [id])

  const analysisTabs = [
    { value: 'back', icon: Search, label: t('publicOption.backAnalysis'), desc: '溯源分析 - 追溯事件起源和传播路径' },
    { value: 'context', icon: GitBranch, label: t('publicOption.eventContext'), desc: '事件脉络 - 事件发展时间线和关键节点' },
    { value: 'trace', icon: TrendingUp, label: t('publicOption.eventTrace'), desc: '事件跟踪 - 持续监控事件最新动态' },
    { value: 'hot', icon: BarChart3, label: t('publicOption.hotAnalysis'), desc: '热点分析 - 事件热度和关注趋势' },
    { value: 'netizens', icon: Users, label: t('publicOption.netizensAnalysis'), desc: '重点网民分析 - 关键意见领袖和观点分布' },
    { value: 'statistics', icon: BarChart3, label: t('publicOption.statistics'), desc: '统计数据 - 全面数据统计概览' },
    { value: 'propagation', icon: Share2, label: t('publicOption.propagationAnalysis'), desc: '传播分析 - 传播路径和影响力评估' },
    { value: 'thematic', icon: FolderTree, label: t('publicOption.thematicAnalysis'), desc: '专题分析 - 深度专题研究' },
    { value: 'unscramble', icon: BookOpen, label: t('publicOption.unscrambleContent'), desc: '内容解读 - 专业视角的内容解读' },
    { value: 'judicial', icon: Scale, label: t('publicOption.judicialOpinion'), desc: '司法舆情 - 司法领域舆情分析研判' },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => { window.location.href = '/publicoption' }}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-xl font-semibold">{t('publicOption.reportDetail')}</h2>
        <Badge variant="secondary">{eventData?.eventname || `ID: ${id}`}</Badge>
      </div>

      {eventData && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{t('publicOption.eventKeywords')}</p>
              <p className="mt-1 text-sm font-medium">{eventData.eventkeywords || '-'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{t('publicOption.eventStartTime')}</p>
              <p className="mt-1 text-sm font-medium">{eventData.eventstarttime || '-'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{t('publicOption.eventEndTime')}</p>
              <p className="mt-1 text-sm font-medium">{eventData.eventendtime || '-'}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{t('common.status')}</p>
              <Badge variant={eventData.status === 1 ? 'success' : 'warning'} className="mt-1">
                {eventData.status === 1 ? t('publicOption.statusSuccess') : t('publicOption.statusCreating')}
              </Badge>
            </CardContent>
          </Card>
        </div>
      )}

      {loading && (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      <Tabs defaultValue="back">
        <TabsList className="flex w-full flex-wrap h-auto">
          {analysisTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              <tab.icon className="mr-1.5 h-3.5 w-3.5" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {analysisTabs.map((tab) => (
          <TabsContent key={tab.value} value={tab.value}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <tab.icon className="h-5 w-5 text-muted-foreground" />
                  {tab.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex h-64 items-center justify-center text-muted-foreground flex-col gap-3">
                  <tab.icon className="h-16 w-16 opacity-20" />
                  <p>{t('common.noData')}</p>
                  <p className="text-xs opacity-60 max-w-md text-center">{tab.desc}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
